// components/portal/Portal.tsx
'use client';

import {
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
  memo,
} from 'react';
import dynamic, { type DynamicOptionsLoadingProps } from 'next/dynamic';
import { useKernel } from '@/kernel/Kernel';
import { canAccessAnalyst } from '#lib/access/analystAccess';
import { canAccessFootfall as canAccessFootfallTool } from '#lib/access/footfallAccess';
import { patchPulse, refreshShortlist } from '@/components/footfall/pulseState';
import { GlobalChatList } from '@/components/portal/GlobalChatList';
import { ChatSearchModal } from '@/components/portal/ChatSearchModal';
import ChatTab from '@/components/ChatTab';
import MasterConsole from '@/components/master/MasterConsole';
import ResizablePanel, { ResizablePanelRef } from '@/components/ui/ResizablePanel';
import { readDevicePreference, readStudioBarMemory, resolveBarCollapse, writeDevicePreference } from '#lib/devicePreferences';
import type { PortalView } from '#types/types';
import { useKernelStore } from '@/kernel/kernelStores';
import { useUser } from '@clerk/nextjs';
import clsx from 'clsx';
import axios from 'axios';
import { ChatContextDashboard } from './ChatContextDashboard';
import { FactoryScreen } from '../marketing/FactoryScreen';
import { PortalInventoryView } from './PortalInventoryView';
import { useTablesStore } from '@/components/tables/tablesStore';
import { useSlidesStore } from '@/components/slides/slidesStore';
import { resolveProjectIdFromChatSessionContext } from '#lib/leasing/chat-session-project-focus';
import { canAccessSlides, isSlidesTrialUser } from '#lib/access/slidesAccess';
import { canAccessFater3D } from '#lib/access/fater3dAccess';
import * as fater3d from '#lib/modelist/fater3dState';
import { projectSceneOf } from '#lib/modelist/projectScene';
import { canAccessSource as canAccessSourceTool, isSourcePilotUser } from '#lib/access/sourceAccess';
import { SourceTrialWelcome } from '@/components/portal/SourceTrialNotice';
import { toast } from 'sonner';
import { SOURCE_EMPTY_PORTFOLIO_ID } from './SourcingCenterView';
import { isTableFocusSubject, resolvePortalViewFromTableFocus, resolveSessionTableFocusView, selectActiveSubjectFromInventoryState, sessionModuleBelongsToView } from '#lib/leasing/session-focus';
import { resolveDefaultSourcePortfolioId } from '#lib/source-defaults';
import { sourceStateApplies } from '#lib/leasing/sourceAgentMode';
import { resolveOpenCampaignScope, sourceDashboardOwnsCanvas, sourceRentRollOwnsCanvas } from '#lib/leasing/source-view-guards';
import { canAccessMasterAgent, resolveLandingView } from '#lib/access/masterAgentAccess';
import { canAccessDaimon } from '#lib/access/daimonAccess';
import { isLegacyTeams2DUser } from '#lib/organisation-teams/legacy-teams-2d-access';
import { canAccessPortalView, isDesignOnlyActivities, normalizeUserActivities, LEGACY_COMPAT_USER_ACTIVITIES } from '#lib/user-activities';
import { UrlSynchronizer } from './PortalUrlSynchronizer';
import { parsePortalUrlState } from '#lib/leasing/portal-url-state';
import { PortalCanvasTopBar } from './PortalCanvasTopBar';
import { PageEnter } from '@/components/ui/PageEnter';
import { SourceCampaignLoading } from './SourceCampaignLoading';
import { PortalHomeMenu } from './PortalHomeMenu';
import { canAccessStudioKind, resolveProjectGenerationViewMode, resolveProjectStudioSection, restoreProjectToolView } from '#lib/studio-project-view';
import type { StudioProjectKind } from '#types/types';
import { OrganisationsTable } from '../entities/tables/organisations/OrganisationsTable';

const PortalDeferredSurfaceFallback = ({ error, retry }: DynamicOptionsLoadingProps) => (
  <div
    className="flex h-full min-h-[160px] w-full flex-col items-center justify-center gap-3 bg-black p-4 text-center text-sm text-white/60"
    role="status"
    aria-live="polite"
    aria-busy={!error}
  >
    <span>{error ? 'This workspace could not load.' : 'Loading workspace…'}</span>
    {error && (
      <button
        type="button"
        onClick={() => retry?.()}
        disabled={!retry}
        className="rounded-md border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs text-white/80 transition-colors hover:bg-white/[0.08] disabled:cursor-not-allowed disabled:opacity-50"
      >
        Retry
      </button>
    )}
  </div>
);

// These tools are only useful after their Portal predicate is true. Keeping their loaders at
// module scope gives Next stable chunks while leaving the Studio project grid free of their heavy
// editors, media libraries, and renderers on first visit.
const TablesWorkspace = dynamic(
  () => import('@/components/tables/TablesWorkspace').then((module) => module.TablesWorkspace),
  { ssr: false, loading: PortalDeferredSurfaceFallback }
);
const FinanceView = dynamic(
  () => import('./FinanceView'),
  { ssr: false, loading: PortalDeferredSurfaceFallback }
);
// Fater 3D carries three.js: loaded only when the module is opened.
const Fater3DView = dynamic(
  () => import('@/components/fater3d/Fater3DView'),
  { ssr: false, loading: PortalDeferredSurfaceFallback }
);
// Video carries the HTML parser of its timeline: loaded only when the module is opened.
const VideoView = dynamic(
  () => import('@/components/video/VideoView').then((module) => module.VideoView),
  { ssr: false, loading: PortalDeferredSurfaceFallback }
);
const Fater3DSceneChat = dynamic(
  () => import('@/components/fater3d/Fater3DView').then((module) => module.Fater3DSceneChat),
  { ssr: false, loading: PortalDeferredSurfaceFallback }
);
const BrandBaseView = dynamic(
  () => import('./BrandBaseView'),
  { ssr: false, loading: PortalDeferredSurfaceFallback }
);
const MarketingBatchCanvas = dynamic(
  () => import('@/components/marketing/MarketingBatchCanvas').then((module) => module.MarketingBatchCanvas),
  { ssr: false, loading: PortalDeferredSurfaceFallback }
);
const ProjectRightSidebarGraphics = dynamic(
  () => import('./ProjectRightSidebarGraphics').then((module) => module.ProjectRightSidebarGraphics),
  { ssr: false, loading: PortalDeferredSurfaceFallback }
);
const SlidesRightSidebar = dynamic(
  () => import('@/components/slides/SlidesRightSidebar'),
  { ssr: false, loading: PortalDeferredSurfaceFallback }
);
const PlanTopBar = dynamic(
  () => import('@/components/footfall/PlanTopBar').then((module) => module.PlanTopBar),
  { ssr: false },
);
const PulseRightSidebar = dynamic(
  () => import('@/components/footfall/PulseRightSidebar').then((module) => module.PulseRightSidebar),
  { ssr: false, loading: PortalDeferredSurfaceFallback }
);

const ADMIN_SECTIONS = {
  organisations: 'workspace-general',
  globalActivity: 'admin-activity',
  finance: 'admin-finance',
  brandBase: 'admin-brands',
} as const;

// A remembered-open Studio right bar spawns where a BottomBar open from collapsed lands: its minimum.
const RIGHT_ACTION_PANEL_MIN_WIDTH_PX = 240;

const PortalComponent = () => {
  const { kernel } = useKernel();
  const userContext = useKernelStore((state) => state.userContext);
  const canAccessAssetManagement = canAccessAnalyst(userContext);
  const canAccessFootfall = canAccessFootfallTool(userContext);
  const canAccessFactory = Boolean(userContext.permissions?.canAccessFactory);
  const activeChatSessionId = useKernelStore((state) => state.activeChatSessionId);
  const setGlobalScrollTop = useKernelStore((state) => state.setGlobalScrollTop);
  const activeTableView = useKernelStore((state) =>
    resolvePortalViewFromTableFocus(selectActiveSubjectFromInventoryState(state))
  );
  const userPreferences = useKernelStore((state) => state.userPreferences);
  const organisationDetails = useKernelStore((state) => {
    const orgId = state.userContext.organisationId;
    return orgId ? state.inventory.Organisation.registry[orgId] : null;
  });
  const accessibleOrganisationSignature = useKernelStore((state) => {
    return Object.values(state.inventory.Organisation.registry)
      .map((org) => `${org.id}:${org.name || ''}`)
      .sort((left, right) => left.localeCompare(right))
      .join('|');
  });
  const { isSignedIn, isLoaded: isUserLoaded } = useUser();
  const [dashboardArrivalNotified, setDashboardArrivalNotified] = useState(false);

  // Onboarding check
  useEffect(() => {
    if (!isUserLoaded || !isSignedIn || dashboardArrivalNotified) return;
    let isCancelled = false;
    const notifyBackend = async () => {
      try {
        await axios.post('/api/user/check-onboarding-status');
      } catch (err) {
        console.error('[Onboarding] Failed notification:', err);
      } finally {
        if (!isCancelled) setDashboardArrivalNotified(true);
      }
    };
    notifyBackend();
    return () => { isCancelled = true; };
  }, [isUserLoaded, isSignedIn, dashboardArrivalNotified]);

  // Auto-maintain the Master "Your chats" list: (1) drop abandoned EMPTY sessions
  // (a "New chat" you created but never used) and (2) retroactively LLM-title any
  // Master session that has real content but is still default-named ("New chat"…).
  // Runs a short delay after mount and whenever the active session changes (i.e.
  // after you navigate away from one). The active session and anything created in
  // the last few minutes are never touched, so a chat you're about to type into is
  // safe. Empty sessions have zero messages, so deleting them loses nothing; the
  // server DELETE also enforces ownership. maybeAutoTitleSession self-guards (it
  // only renames a still-default name that has real user content), so it is safe to
  // call across every session each sweep.
  useEffect(() => {
    if (!kernel.current) return;
    const EMPTY_SESSION_GRACE_MS = 3 * 60 * 1000; // never delete a session created in the last 3 minutes
    const timer = setTimeout(() => {
      const k = kernel.current;
      if (!k) return;
      const reg = k.getInventory().ChatSession.registry;
      const activeId = k.getActiveChatSessionId();
      const uid = k.getUserContext?.()?.dbUserId ?? null;
      const now = Date.now();
      // Re-title EVERY master session from its conversation once (persisted in the user
      // preferences so it does not re-run and churn LLM calls on each reload, on each
      // browser or on each machine). After the one-time force pass a session only gets
      // re-titled while still default-named (the normal path). The registry only grows:
      // dropping an id would replay the force pass on it and overwrite a manual rename.
      const storedRetitled = k.getUserPreferences().retitledMasterSessionIds;
      // Not an array = preferences not hydrated yet (or a failed load): truth unknown, so
      // force nothing and write nothing. Deleting empty sessions is unaffected.
      const isRetitledRegistryKnown = Array.isArray(storedRetitled);
      const retitled = new Set<string>(isRetitledRegistryKnown ? storedRetitled : []);
      let retitledChanged = false;
      for (const session of Object.values(reg)) {
        if (!session || session.creationModule !== 'master') continue;
        if (uid && session.ownerId && session.ownerId !== uid) continue;
        if (session.id === activeId) continue;
        if ((session.messageCount ?? 0) === 0) {
          const createdMs = session.createdAt ? new Date(session.createdAt).getTime() : now;
          if (now - createdMs > EMPTY_SESSION_GRACE_MS) {
            void k.deleteChatSessionSilently?.(session.id);
          }
        } else {
          const force = isRetitledRegistryKnown && !retitled.has(session.id);
          void k.maybeAutoTitleSession?.(session.id, { force });
          if (force) {
            retitled.add(session.id);
            retitledChanged = true;
          }
        }
      }
      if (retitledChanged) {
        void k.updateAndSaveUserPreference('retitledMasterSessionIds', [...retitled]);
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [kernel, activeChatSessionId]);

  // Portal never renders on the server (the page waits for kernelReady), so the lazy read cannot desynchronise hydration.
  const [isHomeMenuCollapsed, setIsHomeMenuCollapsed] = useState(() => readDevicePreference('portalHomeMenuCollapsed'));
  const [isChatSearchOpen, setIsChatSearchOpen] = useState(false);
  // An Analyst session restores its tool after reload; the open document stays transient.
  const activeStandaloneView = useKernelStore((s) => s.portalStandaloneView ?? (
    canAccessAssetManagement && s.activeChatSessionId
      && s.inventory.ChatSession.registry[s.activeChatSessionId]?.creationModule === 'analyst'
      ? 'tables' : null
  ));
  const setActiveStandaloneView = useKernelStore((s) => s.setPortalStandaloneView);
  const lastAppliedDealerOrgRef = useRef<string | null>(null);
  const designOnlyLandingAppliedRef = useRef(false);
  // Explicit URL navigation owns arrival until its session and metadata have loaded.
  const [isRestoringUrlSession, setIsRestoringUrlSession] = useState(() => {
    const entry = parsePortalUrlState(window.location.search);
    return Boolean(entry.chatSessionId && !entry.focusTarget);
  });

  const userActivities = useMemo(
    () => normalizeUserActivities(userPreferences.activities, {
      fallback: LEGACY_COMPAT_USER_ACTIVITIES,
    }),
    [userPreferences.activities]
  );
  // Source is a PILOT: a closed allowlist of individual users, exactly like Slides. The activity
  // grant stays as the outer condition, it is org-level and still governs who could ever have it
  //, but the allowlist is what actually opens the tool while the pilot runs.
  // The allowlist IS the gate, exactly as it is for Slides, NOT the allowlist AND the activity.
  // Checked against the live database: Benoist does not carry the SOURCE activity, so requiring
  // both would have granted him the pilot and then locked him out of it. The activity is an
  // org-level grant and cannot express "these two people".
  const canAccessSource = canAccessSourceTool(userContext);
  const isSourceTrial = isSourcePilotUser(userContext);
  const canAccessDesign = userActivities.includes('DESIGN');
  const isDesignOnlyUser = isDesignOnlyActivities(userActivities);
  const hasAdminAccess = Boolean(userContext?.isFaterAdmin || userContext?.isOrganisationAdmin);
  // Slides is restricted to a closed allowlist of individual accounts. See
  // lib/access/slidesAccess.ts for why this is an allowlist and not an activity.
  const canAccessSlidesTool = canAccessSlides(userContext);
  const canAccessFater3DModule = canAccessFater3D(userContext);
  const canAccessVideoModule = userContext?.canAccessVideo === true;
  const isSlidesTrial = isSlidesTrialUser(userContext);

  const accessibleOrganisations = useMemo(() => {
    const organisationRegistry = kernel.current?.getInventory().Organisation.registry ?? {};
    const ordered = Object.values(organisationRegistry)
      .filter((o): o is NonNullable<typeof o> => !!o)
      .map((org) => ({ id: org.id, name: org.name }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

    const selectedId = userContext?.organisationId || null;
    if (selectedId && !ordered.some((org) => org.id === selectedId)) {
      ordered.unshift({ id: selectedId, name: organisationDetails?.name || 'Organisation' });
    }
    return ordered;
  }, [accessibleOrganisationSignature, kernel, userContext?.organisationId, organisationDetails?.name]);

  const neutralFaterOrganisation = useMemo(() => {
    return accessibleOrganisations.find(
      (org) => typeof org.name === 'string' && org.name.trim().toLowerCase() === 'fater'
    ) ?? null;
  }, [accessibleOrganisations]);

  const isNeutralFaterContext = Boolean(
    userContext?.isFaterAdmin &&
    !userContext?.isImpersonating &&
    !userContext?.organisationId
  );

  const homeSelectedOrganisationId = isNeutralFaterContext
    ? (neutralFaterOrganisation?.id ?? null)
    : (userContext?.organisationId || null);

  const homeOrganisationLabel = isNeutralFaterContext
    ? (neutralFaterOrganisation?.name || 'Fater')
    : (organisationDetails?.name || 'Organisation');

  const handleOpenOrganisationPage = useCallback(() => {
    const orgId = typeof homeSelectedOrganisationId === 'string' ? homeSelectedOrganisationId.trim() : '';
    if (orgId) {
      void kernel.current?.focusEntityUI('Organisation', orgId);
      return;
    }
    if (userContext?.isFaterAdmin) {
      void kernel.current?.focusTableUI('organisations');
    }
  }, [homeSelectedOrganisationId, kernel, userContext?.isFaterAdmin]);

  // NAVIGATION IS A BARRIER FOR WORK STARTED BY THE PREVIOUS SURFACE.
  //
  // Entering Source lands asynchronously (it queries the user's assets, then the org's
  // default portfolio) and then stamps a sourcing subject on the session. Nothing used to
  // cancel that: clicking Source and then Analyst before the queries came back let the
  // landing complete UNDER the Analyst view, which is how the two surfaces ended up on
  // screen together. Every nav entry point bumps this counter; async landings capture it
  // and drop what they were about to apply if it moved.
  const navGenerationRef = useRef(0);
  const beginNavigation = useCallback(() => {
    navGenerationRef.current += 1;
    kernel.current?.setState({ sourceOpeningCampaignId: null, sourceOpeningRentRollId: null });
    return navGenerationRef.current;
  }, []);

  const handlePortalNavClick = useCallback(
    (view: PortalView, studioDestination: StudioProjectKind = 'canvas') => {
      const k = kernel.current;
      if (!k) return;
      if ([...document.querySelectorAll<HTMLElement>('dialog[open], [role="dialog"][aria-modal="true"]')].some(modal => modal.getClientRects().length > 0)) return;
      if (view === 'footfall' && !canAccessFootfall) return;
      if (view === 'projects' && (!canAccessDesign || !canAccessStudioKind(studioDestination, k.getUserContext().permissions))) return;
      const navGeneration = beginNavigation();
      const currentSubject = selectActiveSubjectFromInventoryState(k.getState());
      const returnFromFactory = view === 'projects' && studioDestination === 'list' && !activeStandaloneView && currentSubject?.entityType === 'Project' && k.getStudioSection() === 'factory';
      k.setStudioSection(view === 'projects' ? studioDestination : 'canvas');
      if (returnFromFactory) return;

      // A session must never be carried into a tool it does not belong to. This
      // guard used to name Master only, so Master was the one pair that could not
      // leak and every other pair could: walking from a Studio project into
      // Slides kept the Studio session ACTIVE, which put that project's name in
      // the header and its generation prompts ("Nano Banana Pro launched on
      // workspace 2...") in the left panel, next to the deck browser. The Slides
      // surface had explicitly asked for no chat until a deck is open; it lost,
      // because showEditorChatPanel opens on `activeSubjectEntityType ===
      // 'Project'` as an OR alternative, which outranks that gate.
      //
      // Leave the session rather than reuse it: the tool then creates its own
      // scoped one, and the previous conversation stays in Your chats.
      const activeBeforeNav = k.getActiveChatSessionId();
      if (activeBeforeNav) {
        const activeModule = k.getInventory().ChatSession.registry[activeBeforeNav]?.creationModule;
        if (activeModule && !sessionModuleBelongsToView(activeModule, view)) {
          k.clearActiveChatSession();
        }
      }

      // Leaving a surface also releases the shared scroll global. globalScrollTop
      // is written on scroll by several surfaces (ViewSection, GlobalChatList and
      // the chat panel) and consumed by the home top bar as
      // `translateY(-globalScrollTop)`. Nothing reset it, so the offset of the
      // surface you LEFT was applied to the one you arrived on: scrolling a Studio
      // project chat and clicking Slides rendered the Slides top bar at y=-601,
      // putting the credit balance, notifications and account menu off-screen and
      // out of reach. Measured, not guessed; see the commit message.
      //
      // This sits here rather than in each writer's unmount because navigation is
      // the surface boundary, and because the chat panel's own file cannot be
      // edited without dragging in 14 pre-existing lint errors unrelated to this
      // bug. A writer-side reset stays the better shape the day that file is clean.
      setGlobalScrollTop(0);

      // The admin pages are Settings' Admin group (owner, 2026-09-29: 'Admin is just another theme
      // in the general settings').
      const adminSection = ADMIN_SECTIONS[view as keyof typeof ADMIN_SECTIONS];
      if (adminSection) {
        if (hasAdminAccess) kernel.current?.openModal('UserSettings', { section: adminSection });
        return;
      }

      const isAdminBypassView =
        view === 'audits' || view === 'globalActivity' || view === 'doctrine';
      if (!canAccessPortalView(userActivities, view) && !(hasAdminAccess && isAdminBypassView)) {
        if (!canAccessDesign) {
          return;
        }
        view = 'projects';
      }

      if (view === 'projects' && !canAccessDesign) {
        return;
      }

      // Leaving the Analyst tool closes the open table (re-entry shows the
      // dashboard, like Studio) and unscopes the agent from it.
      if (view !== 'tables') {
        useTablesStore.getState().closeDoc();
      }
      // Leaving the Slides tool closes the open deck the same way (re-entry
      // shows the Slides dashboard) and unscopes the Reviewer agent from it.
      if (view !== 'slides') {
        useSlidesStore.getState().closeDeck();
      }

      if (view === 'footfall') {
        k.clearActiveChatSession();
        setActiveStandaloneView('footfall');
        k.logUserAction('Footfall opened');
        return;
      }
      if (view === 'globalActivity') {
        setActiveStandaloneView('globalActivity');
        return;
      }
      if (view === 'doctrine') {
        // Every user may open Doctrine, the view scopes its own content to the
        // user's rights (personal instruction + memory for all; org/tool doctrine
        // for admins). No admin gate here.
        setActiveStandaloneView('doctrine');
        // The Doctrine view carries the Master agent in a side chat: the user
        // prompts it to fill in their doctrine files (READ_DOCTRINE /
        // UPDATE_DOCTRINE). Resume the newest doctrine-scoped session, or
        // create one, sessions with creationModule 'doctrine' route to the
        // master brain without opening the MasterConsole.
        const activeId = k.getActiveChatSessionId();
        const registry = k.getInventory().ChatSession.registry;
        const activeIsDoctrine = !!activeId && registry[activeId]?.creationModule === 'doctrine';
        if (!activeIsDoctrine) {
          const newestDoctrineSession = Object.values(registry)
            .filter((s) => s?.creationModule === 'doctrine')
            .sort((a, b) => (b?.updatedAt ?? '').localeCompare(a?.updatedAt ?? ''))[0];
          void (async () => {
            if (newestDoctrineSession?.id) {
              await k.activateChatSession(newestDoctrineSession.id);
            } else {
              await k.createAndActivateSession({
                nameHint: 'Doctrine',
                creationScope: { module: 'doctrine' },
                // Doctrine filling is a keep-momentum flow: read the file,
                // rewrite it, confirm, never pause mid-flow for approval.
                collaborationMode: 'AUTONOMOUS',
              });
            }
          })();
        }
        return;
      }
      // Build (Fater 3D) keeps its projects in conversations of its own (lib/modelist/modelist.server.ts), read by the
      // module itself: no chat session of the portal goes with it.
      if (view === 'fater3d') {
        if (!canAccessFater3DModule) return;
        k.clearActiveChatSession();
        // As every module's entry, it lands on the module's dashboard.
        k.setFater3d(fater3d.home);
        setActiveStandaloneView(view);
        return;
      }
      // Video keeps its films in conversations of its own (lib/video/video.server.ts), read by the module itself.
      if (view === 'video') {
        if (!canAccessVideoModule) return;
        k.clearActiveChatSession();
        k.setVideo((v) => ({ ...v, filmId: null, film: null, html: null }));
        setActiveStandaloneView('video');
        return;
      }
      if (view === 'slides') {
        if (!canAccessSlidesTool) {
          return;
        }
        setActiveStandaloneView('slides');
        // No session work here. The chat belongs to a DECK, not to the tool:
        // the effect below binds one session per open deck. Resuming "the
        // newest slides session" on nav is what used to carry deck A's
        // conversation into deck B, including its agent context, so the
        // Reviewer would answer questions about the wrong document.
        return;
      }
      if (view === 'tables') {
        if (!canAccessAssetManagement) {
          toast.error('Analyst is disabled for your account.');
          return;
        }
        setActiveStandaloneView('tables');
        // Keep (or open) a chat session so the agent panel is shown by default,
        // and render the Analyst workspace in the editor layout's main area.
        void (async () => {
          let sessionId = k.getActiveChatSessionId();
          const activeSession = sessionId ? k.getInventory().ChatSession.registry[sessionId] ?? null : null;
          if (!sessionId || !activeSession) {
            sessionId = await k.ensureSession({
              nameHint: 'Analyst',
              // Analyst has no entity subject, stamp its home explicitly.
              creationScope: { module: 'analyst', entityType: null, entityId: null },
            });
          }
          if (!sessionId) return;
          if (k.getActiveChatSessionId() !== sessionId) {
            await k.activateChatSession(sessionId);
          }
          // No entity subject, Analyst is a standalone tool, so the chat is general.
          k.setActiveSubject(sessionId, null);
        })();
        return;
      }

      if (activeStandaloneView) {
        setActiveStandaloneView(null);
        if (view === 'dashboard') {
          return;
        }
      }

      const tableView = resolveSessionTableFocusView(view);
      if (!tableView) {
        if (view === 'dashboard') {
          k.clearActiveChatSession();
        }
        return;
      }

      // One implementation of "open a table", in the kernel. This used to be a second copy that
      // handed the table focus to whatever session was active, which is precisely the shape
      // focusTableUI was fixed to stop doing: it stamped a table subject onto a session that
      // belonged elsewhere, or was refused outright and left the folder inert.
      const isStillWanted = () => navGenerationRef.current === navGeneration && k.getStudioSection() !== 'factory';
      void k.focusTableUI(tableView, { isStillWanted }).then(() => {
        if (view === 'projects' && isStillWanted()) k.setStudioSection(studioDestination);
      });
    },
    [
      activeStandaloneView,
      beginNavigation,
      canAccessAssetManagement,
      canAccessDesign,
      canAccessFootfall,
      canAccessSlidesTool,
      setGlobalScrollTop,
      hasAdminAccess,
      kernel,
      userActivities,
    ],
  );

  useEffect(() => {
    if (!canAccessFootfall && activeStandaloneView === 'footfall') setActiveStandaloneView(null);
  }, [activeStandaloneView, canAccessFootfall, setActiveStandaloneView]);

  // Pulse: the dashboard is full width; an open plan is an editor with its agent on the left and its panel on the
  // right, and one private conversation per plan, bound the way Slides binds one per deck.
  const pulsePlanId = useKernelStore((s) => s.pulse.planId);
  // A plan's conversation IS the Plan surface: the view state is not in the URL, so on a reload the restored
  // conversation would otherwise land on a blank editor layout with no tool in it. Same rule as Slides.
  const isPulseSession = useKernelStore((s) => {
    const id = s.activeChatSessionId;
    return !!id && s.inventory.ChatSession.registry[id]?.creationModule === 'pulse';
  });
  useEffect(() => {
    if (isPulseSession && canAccessFootfall && activeStandaloneView !== 'footfall') setActiveStandaloneView('footfall');
  }, [isPulseSession, canAccessFootfall, activeStandaloneView, setActiveStandaloneView]);
  const pulsePlanOpen = canAccessFootfall && activeStandaloneView === 'footfall' && !!pulsePlanId;
  const pulseSessionBoundRef = useRef<string | null>(null);
  useEffect(() => {
    const k = kernel.current;
    if (!k || !pulsePlanOpen || !pulsePlanId) { pulseSessionBoundRef.current = null; return; }
    if (pulseSessionBoundRef.current === pulsePlanId) return;
    pulseSessionBoundRef.current = pulsePlanId;
    const planId = pulsePlanId;
    refreshShortlist(k);
    void (async () => {
      const active = k.getActiveChatSessionId();
      const session = active ? k.getInventory().ChatSession.registry[active] : null;
      if (session?.creationModule === 'pulse' && session.creationEntityId === planId) return;
      const { ids, error } = await k.queryInventory('ChatSession', { filters: { creationModule: 'pulse', creationEntityType: 'PulsePlan', creationEntityId: planId }, limit: 50, force: true, ttl: 0 });
      if (k.getState().pulse.planId !== planId) return;
      const registry = k.getInventory().ChatSession.registry;
      const home = (error ? [] : ids).filter((id) => registry[id]).sort((a, b) => new Date(registry[a]!.createdAt || 0).getTime() - new Date(registry[b]!.createdAt || 0).getTime())[0];
      if (home) { if (k.getActiveChatSessionId() !== home) await k.activateChatSession(home); return; }
      await k.createAndActivateSession({
        nameHint: k.getState().pulse.draft?.name || 'Merch',
        creationScope: { module: 'pulse', entityType: 'PulsePlan', entityId: planId },
        collaborationMode: 'AUTONOMOUS',
      });
    })();
  }, [pulsePlanOpen, pulsePlanId, kernel]);

  const handleFootfallSource = useCallback((propertyId: string) => {
    const k = kernel.current;
    if (!k || !canAccessSource) return;
    beginNavigation();
    useTablesStore.getState().closeDoc();
    useSlidesStore.getState().closeDeck();
    k.logUserAction('Footfall asset opened in Source', { propertyId });
    void k.focusEntityUI('Property', propertyId, 'RENT_ROLL').catch(error => {
      if (k.getState().sourceOpeningRentRollId === propertyId) k.setState({ sourceOpeningRentRollId: null });
      k.handleError(error instanceof Error ? error.message : 'Could not open asset in Source.');
    });
  }, [beginNavigation, canAccessSource, kernel]);

  useEffect(() => {
    if (canAccessAssetManagement) return;
    if (activeStandaloneView !== 'tables') return;
    useTablesStore.getState().closeDoc();
    setActiveStandaloneView(null);
  }, [activeStandaloneView, canAccessAssetManagement]);

  // Bridge: when the agent opens an Analyst table (ANALYST_OPEN_TABLE bumps the
  // store's viewOpenNonce), bring the Analyst view on-screen so the user sees it.
  // Set the standalone view directly (not handlePortalNavClick) so it can't race
  // with a chat turn's subject/session juggling and immediately revert ("quit").
  const analystViewNonce = useTablesStore((s) => s.viewOpenNonce);
  // Whenever a table is actually open in the Analyst store, render the Analyst ,
  // this is the robust source of truth (the agent's open/create sets activeDocId),
  // independent of the view-nonce bridge which can race with a chat turn.
  const analystDocOpen = useTablesStore((s) => !!s.activeDocId);
  // A master session is a SEPARATE construct, the orchestrator's operator view.
  // It renders its own MasterConsole, never the Analyst grid, even while it builds
  // a table in the shared store (which sets activeDocId). Defined here so both the
  // view-open-nonce bridge below and the render guards can suppress the takeover.
  // Slides: the left Reviewer chat appears only once a deck is open (the file
  // browser is full-width, like Figma's).
  const activeSlidesProjectId = useSlidesStore((s) => s.activeProjectId);
  const pendingSlidesFocus = useSlidesStore((s) => s.pendingFocus);
  const slidesDeckIsOpen = !!activeSlidesProjectId;
  // A slides-scoped chat session IS the Slides surface. activeStandaloneView is
  // transient React state (lost on reload / re-render), so deriving the surface
  // from the session keeps the editor mounted instead of dropping the user onto
  // a bare full-width chat page with no canvas or right bar.
  const isSlidesSession = useKernelStore((s) => {
    const id = s.activeChatSessionId;
    if (!id) return false;
    return s.inventory.ChatSession.registry[id]?.creationModule === 'slides';
  });
  const isMasterSession = useKernelStore((s) => {
    const id = s.activeChatSessionId;
    if (!id) return false;
    return s.inventory.ChatSession.registry[id]?.creationModule === 'master';
  });
  // Defence in depth: a slides-scoped session renders the editor on its own, so
  // the pilot gate must cover the SURFACE, not just the nav button.
  const isSlidesSurface = canAccessSlidesTool && (activeStandaloneView === 'slides' || isSlidesSession);


  // One chat session PER DECK. A slides session's home is its deck's Project
  // (creationScope entityType/entityId), which is also how the Reviewer's turn
  // context resolves which deck it is looking at, so a session shared across
  // decks made the agent describe the wrong document.
  //
  // Resolution is SERVER-authoritative (same pattern as focusEntityUI in
  // useLeasingWorkflows): reading the client registry as it stands raced session
  // hydration, and combined with StrictMode's double-invoked effects it minted a
  // fresh empty session on every open, which is how a deck's conversation
  // appeared to vanish. queryInventory is that server round-trip, and it hydrates
  // the registry (messageCount included) before we read it back. Prefer a scoped
  // session that already holds messages, else the OLDEST one (a newer empty
  // duplicate must never win), else create the home.
  const slidesSessionBoundRef = useRef<string | null>(null);
  useEffect(() => {
    const k = kernel.current;
    if (!k || !activeSlidesProjectId) return;
    if (slidesSessionBoundRef.current === activeSlidesProjectId) return;
    slidesSessionBoundRef.current = activeSlidesProjectId;
    const projectId = activeSlidesProjectId;

    void (async () => {
      const activeId = k.getActiveChatSessionId();
      const activeSession = activeId ? k.getInventory().ChatSession.registry[activeId] ?? null : null;
      if (
        activeSession?.creationModule === 'slides' &&
        resolveProjectIdFromChatSessionContext(activeSession) === projectId
      ) {
        return; // already on this deck's session
      }

      // force/ttl 0: a binding path must never be answered from a stale query cache.
      const { ids, error } = await k.queryInventory('ChatSession', {
        filters: { creationModule: 'slides', creationEntityType: 'Project', creationEntityId: projectId },
        limit: 50,
        force: true,
        ttl: 0,
      });
      // A network failure is not fatal here: no candidate simply means we mint the home.
      const registry = error ? {} : k.getInventory().ChatSession.registry;
      const rows = (error ? [] : ids)
        .map((id) => {
          const session = registry[id];
          return {
            id,
            atMs: new Date(session?.createdAt || session?.updatedAt || 0).getTime(),
            session,
          };
        })
        .filter((row) => Boolean(row.session))
        .sort((a, b) => a.atMs - b.atMs); // oldest first

      let home: string | null = null;
      for (const candidate of rows) {
        const count =
          typeof candidate.session?.messageCount === 'number' && Number.isFinite(candidate.session.messageCount)
            ? candidate.session.messageCount
            : 0;
        const messages = k.getChatSessionMessages?.(candidate.id) ?? [];
        if (count > 0 || messages.some((m) => m.role === 'user' || m.role === 'assistant')) {
          home = candidate.id;
          break;
        }
      }
      home = home ?? rows[0]?.id ?? null;

      if (home) {
        if (k.getActiveChatSessionId() !== home) await k.activateChatSession(home);
        return;
      }

      const deckState = useSlidesStore.getState();
      const deckName =
        deckState.decks[projectId]?.sourceFileName ||
        deckState.deckIndex.find((d) => d.projectId === projectId)?.name ||
        'Slides';
      await k.createAndActivateSession({
        nameHint: deckName,
        creationScope: { module: 'slides', entityType: 'Project', entityId: projectId },
        // A review is a keep-momentum flow: inventory the pages, judge point by
        // point, stamp, never pause mid-pass for approval.
        collaborationMode: 'AUTONOMOUS',
      });
    })();
  }, [activeSlidesProjectId, kernel]);
  const prevAnalystViewNonceRef = useRef(analystViewNonce);
  useEffect(() => {
    if (analystViewNonce === prevAnalystViewNonceRef.current) return;
    prevAnalystViewNonceRef.current = analystViewNonce;
    // A Master session is never pulled into the Analyst grid: it builds tables in
    // the shared store and shows slices inline in its own chat. Consume the nonce
    // but only bring the workspace on-screen outside a Master session.
    if (analystViewNonce > 0 && canAccessAssetManagement && !isMasterSession && (activeStandaloneView === null || activeStandaloneView === 'tables')) {
      setActiveStandaloneView('tables');
    }
  }, [analystViewNonce, canAccessAssetManagement, isMasterSession, activeStandaloneView]);

  // A Master chat's live table card can ask to open its doc in the Analyst editor
  // (its Edit / expand controls). Honor it: leave the master session, open the doc,
  // and show the Analyst workspace (no longer suppressed once the session is gone).
  const openInEditorReq = useTablesStore((s) => s.openInEditor);
  useEffect(() => {
    if (!openInEditorReq || !canAccessAssetManagement) return;
    const req = useTablesStore.getState().consumeOpenInEditor();
    if (!req) return;
    kernel.current?.clearActiveChatSession();
    useTablesStore.getState().openDoc(req.docId);
    if (req.sheet) useTablesStore.setState({ focusSheetName: req.sheet });
    setActiveStandaloneView('tables');
  }, [openInEditorReq, canAccessAssetManagement, kernel]);

  useEffect(() => {
    if (!activeTableView) return;
    const isAdminBypassTableView =
      activeTableView === 'audits' || activeTableView === 'organisations';
    if (canAccessPortalView(userActivities, activeTableView) || (hasAdminAccess && isAdminBypassTableView)) return;
    if (canAccessDesign) {
      handlePortalNavClick('projects');
      return;
    }
    kernel.current?.clearActiveChatSession();
  }, [activeTableView, canAccessDesign, handlePortalNavClick, hasAdminAccess, kernel, userActivities]);

  const handleHomeNewChat = useCallback(() => {
    beginNavigation();
    setActiveStandaloneView(null);
    useTablesStore.getState().closeDoc();
    kernel.current?.clearActiveChatSession();
    setIsChatSearchOpen(false);
  }, [beginNavigation, kernel]);

  // Home: return to the hero landing (GlobalChatList) by clearing the active
  // session + any tool/standalone view. Unlike New session, it does NOT create a
  // master session.
  // Closes a standalone admin view without tearing down the active session, so a row click can
  // dismiss the panel and then focus its subject. handleGoHome would clear the session first.
  const handleCloseStandaloneView = useCallback(() => {
    setActiveStandaloneView(null);
  }, []);

  const handleGoHome = useCallback(() => {
    beginNavigation();
    setActiveStandaloneView(null);
    useTablesStore.getState().closeDoc();
    setIsChatSearchOpen(false);
    kernel.current?.clearActiveChatSession();
  }, [beginNavigation, kernel]);

  const handleHomeSidebarNewChat = useCallback(() => {
    beginNavigation();
    setActiveStandaloneView(null);
    setIsChatSearchOpen(false);

    const k = kernel.current;
    if (!k) return;

    const activeSessionId = k.getActiveChatSessionId();
    const activeSession = activeSessionId
      ? (k.getInventory().ChatSession.registry[activeSessionId] ?? null)
      : null;

    // Already sitting on an empty Master session? Stay there instead of stacking up
    // more empty ones.
    if (activeSessionId && activeSession?.creationModule === 'master') {
      const localMessages = k.getChatSessionMessages(activeSessionId);
      const hasConversationMessages = localMessages.some(
        (message) => message.role === 'user' || message.role === 'assistant'
      );
      const persistedMessageCount =
        typeof activeSession.messageCount === 'number' && Number.isFinite(activeSession.messageCount)
          ? Math.max(0, Math.floor(activeSession.messageCount))
          : 0;
      if (!hasConversationMessages && persistedMessageCount === 0) {
        return;
      }
    }

    // New session IS the Master: create a master-scoped session so it opens the
    // Master console (ChatGPT composer) and routes to the master brain, which
    // executes work in place (RUN_STUDIO_STEP) instead of navigating into a tool.
    void (async () => {
      const createdSessionId = await k.createAndActivateSession({
        nameHint: 'New chat',
        creationScope: { module: 'master' },
      });
      if (!createdSessionId) {
        k.clearActiveChatSession();
      }
    })();
  }, [beginNavigation, kernel]);

  const handleHomeSearchChats = useCallback(() => {
    beginNavigation();
    setActiveStandaloneView(null);
    useTablesStore.getState().closeDoc();
    kernel.current?.clearActiveChatSession();
    setIsChatSearchOpen(true);
  }, [beginNavigation, kernel]);
  // Every change goes through the stored value, so it is the current state too: stable, and no side effect inside an updater.
  const handleToggleHomeMenuCollapse = useCallback(() => {
    const next = !readDevicePreference('portalHomeMenuCollapsed');
    writeDevicePreference('portalHomeMenuCollapsed', next);
    setIsHomeMenuCollapsed(next);
  }, []);
  const handleCloseChatSearchModal = useCallback(() => {
    setIsChatSearchOpen(false);
  }, []);

  const focusSourcePortfolio = useCallback(async (portfolioId: string) => {
    const k = kernel.current;
    const normalizedPortfolioId = typeof portfolioId === 'string' ? portfolioId.trim() : '';
    if (!k || !normalizedPortfolioId) return;

    setActiveStandaloneView(null);
    useTablesStore.getState().closeDoc();

    await k.focusEntityUI('Portfolio', normalizedPortfolioId, 'ORCHESTRATE_SOURCING');
  }, [kernel]);

  const handleHomeSource = useCallback(() => {
    const k = kernel.current;
    if (!k || !canAccessSource) return;

    // Always leave the Analyst/standalone tables view when entering Source. The
    // stored-target path below only swaps the subject; without this the center
    // stays stuck on the tables view (the old workaround was a Studio detour).
    setActiveStandaloneView(null);
    useTablesStore.getState().closeDoc();

    // Leave a Master session so it doesn't keep the Master console rendering over
    // the Source view (and so we never stamp a Source subject onto the master chat).
    const activeBeforeNav = k.getActiveChatSessionId();
    if (activeBeforeNav && k.getInventory().ChatSession.registry[activeBeforeNav]?.creationModule === 'master') {
      k.clearActiveChatSession();
    }

    // Landing on Source takes a round trip or two. If the user has moved to another tool
    // by the time it resolves, the landing is stale: applying it would stamp a sourcing
    // subject under someone else's surface.
    const navGeneration = beginNavigation();
    const stillEnteringSource = () => navGenerationRef.current === navGeneration;

    void (async () => {
      const uc = k.getUserContext?.();
      const myUserId = uc?.dbUserId ?? null;
      const isAdmin = !!(uc?.isFaterAdmin || uc?.isOrganisationAdmin);

      // A leasing manager assigned to exactly one asset lands straight on that asset, their whole
      // job is that one centre, so entering Source should drop them on it, not on a portfolio
      // dashboard. Admins / multi-asset users fall through to the org's default client dashboard.
      if (myUserId && !isAdmin) {
        try {
          const mine = await k.queryInventory('Property', { filters: { managerId: myUserId }, limit: 2, offset: 0 });
          const ids = Array.isArray(mine?.ids) ? mine.ids : [];
          if (ids.length === 1 && typeof ids[0] === 'string') {
            if (!stillEnteringSource()) return;
            await k.focusEntityUI('Property', ids[0], 'ORCHESTRATE_SOURCING');
            return;
          }
        } catch { /* fall through to the default landing */ }
      }
      if (!stillEnteringSource()) return;

      // The org's default Source portfolio (the ACTIVE CLIENT, e.g. ALTAREA) wins next: entering
      // Source is a "go home" action and must land on the client dashboard deterministically.
      const defaultPortfolioId = resolveDefaultSourcePortfolioId(uc?.organisationId ?? null);
      if (defaultPortfolioId) {
        await focusSourcePortfolio(defaultPortfolioId);
        return;
      }

      const storedTarget = k.resolveStoredSourcingTarget();
      const currentSessionId = k.getActiveChatSessionId();
      if (storedTarget && currentSessionId) {
        k.setActiveSubject(currentSessionId, {
          entityType: storedTarget.entityType,
          entityId: storedTarget.entityId,
          focusActionType: 'ORCHESTRATE_SOURCING',
        });
        return;
      }

      // No manager asset, no org default, no stored target → the top portfolio (most assets).
      try {
        const result = await k.queryInventory('Portfolio', {
          filters: { minAssets: 1, sortBy: 'ASSETS', sortDir: 'desc' },
          limit: 1,
          offset: 0,
        });
        const firstPortfolioId = Array.isArray(result?.ids) ? result.ids[0] : undefined;
        if (!stillEnteringSource()) return;
        await focusSourcePortfolio(firstPortfolioId || SOURCE_EMPTY_PORTFOLIO_ID);
      } catch {
        if (!stillEnteringSource()) return;
        await focusSourcePortfolio(SOURCE_EMPTY_PORTFOLIO_ID);
      }
    })();
  }, [activeChatSessionId, beginNavigation, canAccessSource, focusSourcePortfolio, kernel]);

  // ── Land on a module, not on the Master agent ─────────────────────────────────────────────
  // While the Master agent is unreleased, a user who arrives on one of its surfaces (the Home
  // screen, a chat session, the Doctrine view) is moved to the first module they actually have
  // access to. Runs once per mount and again if their access changes; it never fires for someone
  // who can use the agent, and never for a user with no module at all (bouncing them somewhere
  // equally unusable would be worse than leaving them put).
  const masterAgentLockedForUser = !canAccessMasterAgent(userContext);
  const landingAppliedRef = useRef(false);
  useEffect(() => {
    if (!masterAgentLockedForUser || isRestoringUrlSession) return;
    if (landingAppliedRef.current) return;
    // Wait until permissions are actually loaded, or we would redirect on an empty context.
    const hasAnyModule =
      canAccessSource || canAccessDesign || canAccessAssetManagement || canAccessSlidesTool;
    if (!hasAnyModule) return;

    const onMasterSurface =
      (Boolean(activeChatSessionId) && !(canAccessAssetManagement && activeStandaloneView === 'tables')) ||
      activeStandaloneView === 'doctrine' ||
      (!activeStandaloneView && !activeTableView);
    // Home is the Daimon's page: a Daimon pilot stays there (lib/access/daimonAccess.ts).
    const onDaimonHome = canAccessDaimon(userContext) && !activeChatSessionId && !activeStandaloneView && !activeTableView;
    if (!onMasterSurface || onDaimonHome) {
      landingAppliedRef.current = true;
      return;
    }

    const landing = resolveLandingView({
      canAccessDesign,
      canAccessSource,
      canAccessAssetManagement,
      canAccessSlidesTool,
      slidesDeepLink: Boolean(pendingSlidesFocus),
    });
    if (!landing) return;
    landingAppliedRef.current = true;
    kernel.current?.clearActiveChatSession();
    if (landing === 'source') {
      handleHomeSource();
      return;
    }
    handlePortalNavClick(landing);
  }, [
    masterAgentLockedForUser,
    isRestoringUrlSession,
    activeChatSessionId,
    activeStandaloneView,
    activeTableView,
    canAccessSource,
    canAccessDesign,
    canAccessAssetManagement,
    canAccessSlidesTool,
    pendingSlidesFocus,
    handleHomeSource,
    handlePortalNavClick,
    kernel,
  ]);

  // A Slides deep link keeps pulling the app into Slides, on its deck, until SlidesView has
  // applied it: boot-time landings and view resets run after the link was recorded, and
  // several of them close the deck or change the view. Once permissions are known and the
  // user cannot use Slides, the link is dropped rather than left waiting forever.
  useEffect(() => {
    if (!pendingSlidesFocus) return;
    const permissionsLoaded = canAccessSource || canAccessDesign || canAccessAssetManagement || canAccessSlidesTool;
    if (!permissionsLoaded) return;
    const slides = useSlidesStore.getState();
    if (!canAccessSlidesTool) { slides.setPendingFocus(null); return; }
    // Only here, with permissions known, does the link open its deck: earlier would bind a
    // Reviewer session to a deck the user may not be allowed to see.
    if (activeSlidesProjectId !== pendingSlidesFocus.projectId) slides.setActiveProject(pendingSlidesFocus.projectId);
    if (activeStandaloneView !== 'slides') handlePortalNavClick('slides');
  }, [pendingSlidesFocus, activeSlidesProjectId, activeStandaloneView, canAccessSlidesTool, canAccessSource, canAccessDesign, canAccessAssetManagement]);

  const handleSelectHomeOrganisation = useCallback(async (orgId: string) => {
    const k = kernel.current;
    if (!k) return;

    const currentOrgId = k.getUserContext().organisationId;
    const normalizedOrgId = typeof orgId === 'string' ? orgId.trim() : '';
    if (!normalizedOrgId || currentOrgId === normalizedOrgId) return;

    await k.applyHomeOrganisationScope(normalizedOrgId, { persistAdminPreference: true });
  }, [kernel]);

  const normalizedCommanderOrganisationId = useMemo(() => {
    const raw = userPreferences.commanderOrganisationId;
    if (typeof raw !== 'string') return '';
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : '';
  }, [userPreferences.commanderOrganisationId]);

  useEffect(() => {
    if (!userContext?.isFaterAdmin || userContext?.isImpersonating) return;
    if (!normalizedCommanderOrganisationId) return;
    if (userContext.organisationId === normalizedCommanderOrganisationId) {
      lastAppliedDealerOrgRef.current = normalizedCommanderOrganisationId;
      return;
    }
    if (lastAppliedDealerOrgRef.current === normalizedCommanderOrganisationId) {
      return;
    }

    lastAppliedDealerOrgRef.current = normalizedCommanderOrganisationId;
    void kernel.current?.applyHomeOrganisationScope(normalizedCommanderOrganisationId, {
      persistAdminPreference: false,
    });
  }, [
    kernel,
    normalizedCommanderOrganisationId,
    userContext?.isFaterAdmin,
    userContext?.isImpersonating,
    userContext?.organisationId,
  ]);

  const handleOpenSearchedSession = useCallback((sessionId: string) => {
    beginNavigation();
    setActiveStandaloneView(null);
    kernel.current?.activateChatSession(sessionId);
    setIsChatSearchOpen(false);
  }, [kernel]);

  const canSeeAudits = hasAdminAccess || Boolean(userContext?.isFaterOrgMember ?? false);
  const canSeePortfolios = canAccessSource && Boolean(userContext?.dbUserId);

  const lastActiveSessionId = useRef<string | null>(null);

  // The left bar is no longer folded when a tool's conversation opens: it stays as the user left it,
  // Recents included, as ChatGPT keeps its sidebar (owner, 2026-09-28).

  useEffect(() => {
    if (designOnlyLandingAppliedRef.current || isRestoringUrlSession) return;
    if (!isDesignOnlyUser || !canAccessDesign) return;
    // The child URL synchronizer can record the deck after this render, before these effects.
    if (canAccessSlidesTool && (pendingSlidesFocus || useSlidesStore.getState().pendingFocus)) {
      designOnlyLandingAppliedRef.current = true;
      return;
    }
    if (activeChatSessionId) {
      designOnlyLandingAppliedRef.current = true;
      return;
    }
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('c')) return;
    }
    designOnlyLandingAppliedRef.current = true;
    handlePortalNavClick('projects');
  }, [activeChatSessionId, canAccessDesign, canAccessSlidesTool, handlePortalNavClick, isDesignOnlyUser, isRestoringUrlSession, pendingSlidesFocus]);
  
  useEffect(() => {
    const prevId = lastActiveSessionId.current;
    const currentId = activeChatSessionId;

    if (prevId && prevId !== currentId) {
      void kernel.current?.handleSessionExit(prevId);
    }
    
    lastActiveSessionId.current = currentId;
  }, [activeChatSessionId, kernel]);

  const studioProjectId = useKernelStore(s => {
    const subject = selectActiveSubjectFromInventoryState(s);
    return subject && !isTableFocusSubject(subject) && subject.entityType === 'Project' ? subject.entityId : null;
  });
  // A Slides deck is a Project row too; only a Studio project owns the remembered bar states.
  const openStudioProjectId = !isSlidesSurface && activeStandaloneView === null ? studioProjectId : null;
  // Once opened, the scene's chat and panel stay mounted under Studio's own, hidden, as its space does under the canvas.
  const projectScene = useKernelStore((s) => (openStudioProjectId ? projectSceneOf(s, openStudioProjectId) : null));
  const sceneChatProjectId = projectScene === 'shown' ? openStudioProjectId : null;
  const sceneMountedProjectId = projectScene ? openStudioProjectId : null;
  const studioLeftBarMemory = readStudioBarMemory('studioLeftBarCollapsed', openStudioProjectId);
  const studioRightBarMemory = readStudioBarMemory('studioRightBarCollapsed', openStudioProjectId);
  const handleStudioLeftBarGesture = useCallback((collapsed: boolean) => {
    if (openStudioProjectId) writeDevicePreference('studioLeftBarCollapsed', collapsed);
    // Closing the Merch chat by hand brings its bubble back.
    if (isPulseSession) patchPulse(kernel.current, { chatOpen: !collapsed });
  }, [openStudioProjectId, isPulseSession, kernel]);
  const handleStudioRightBarGesture = useCallback((collapsed: boolean) => {
    if (openStudioProjectId) writeDevicePreference('studioRightBarCollapsed', collapsed);
  }, [openStudioProjectId]);

  // A project's scene draws its own panel into this column, whichever of the two mounts first.
  const bindScenePanelHost = useCallback((panelHost: HTMLDivElement | null) => kernel.current?.setFater3d((f) => ({ ...f, panelHost })), [kernel]);
  const leftChatPanelRef = useRef<ResizablePanelRef>(null);
  const rightActionPanelRef = useRef<ResizablePanelRef>(null);
  const bindLeftPanelRef = useCallback((panelRef: ResizablePanelRef | null) => {
    leftChatPanelRef.current = panelRef;
    kernel.current?.setLeftPanelRef(panelRef);
  }, [kernel]);
  // One-shot guard for the ref-callback auto-open below. The ResizablePanel's imperative
  // handle is recreated on every collapse/expand (useImperativeHandle deps), and React
  // re-invokes the ref callback each time, so an unguarded auto-open re-fires right after
  // the user explicitly CLOSES the bar in a sourcing context and instantly reopens it
  // (the "handle doesn't close" bug). Track the sourcing context we already auto-opened
  // for: open once per context, then let the user's close stick until the context changes.
  const rightPanelAutoOpenKeyRef = useRef<string | null>(null);
  const bindRightPanelRef = useCallback((panelRef: ResizablePanelRef | null) => {
    rightActionPanelRef.current = panelRef;
    kernel.current?.setRightPanelRef(panelRef);

    if (!panelRef || !kernel.current) return;
    if (studioRightBarMemory !== null) return;

    const activeSessionId = kernel.current.getActiveChatSessionId();
    const activeSubject = activeSessionId
      ? kernel.current.getInventory().ChatSession.registry[activeSessionId]?.activeSubject ?? null
      : null;

    const entitySubject = activeSubject && !isTableFocusSubject(activeSubject) ? activeSubject : null;

    const shouldAutoOpenForSourcing =
      entitySubject?.focusActionType === 'ORCHESTRATE_SOURCING' &&
      (entitySubject.entityType === 'Portfolio' ||
        entitySubject.entityType === 'Property' ||
        entitySubject.entityType === 'Unit');

    // Also auto-open on mount if the user is already in a Source campaign/brand context, handles
    // the case where the Source state is restored BEFORE this panel mounts, which the effect below
    // (keyed on state change) can't catch.
    const sourceState = kernel.current.getState?.();
    const hasSourceContent =
      Boolean(sourceState?.sourceSelectedCampaignId) ||
      Boolean(sourceState?.sourceFocusedFitId) ||
      ((sourceState?.sourceSelectedFitIds?.length ?? 0) > 0);

    const autoOpenKey =
      shouldAutoOpenForSourcing && entitySubject
        ? `${entitySubject.entityType}:${entitySubject.entityId}`
        : hasSourceContent
          ? 'source-content'
          : null;
    if (autoOpenKey && rightPanelAutoOpenKeyRef.current !== autoOpenKey) {
      rightPanelAutoOpenKeyRef.current = autoOpenKey;
      if (panelRef.isCurrentlyCollapsed()) {
        panelRef.toggleCollapse();
      }
    }
  }, [kernel, studioRightBarMemory]);

  const pulseChatOpen = useKernelStore((s) => s.pulse.chatOpen);
  useEffect(() => {
    const panel = leftChatPanelRef.current;
    if (!panel || !activeChatSessionId) return;
    // An active session opens the chat, except a Studio project the user last left closed on this device,
    // and a merch plan, whose chat stays closed until its bubble opens it.
    const collapsed = isPulseSession ? !pulseChatOpen : resolveBarCollapse(studioLeftBarMemory, false);
    if (panel.isCurrentlyCollapsed() !== collapsed) panel.toggleCollapse();
  }, [activeChatSessionId, openStudioProjectId, studioLeftBarMemory, isPulseSession, pulseChatOpen]);

  const isEditorLayout = !!activeChatSessionId;
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(true);
  const activeSubjectEntityType = useKernelStore((s) => {
    const subj = selectActiveSubjectFromInventoryState(s);
    if (!subj || isTableFocusSubject(subj)) return null;
    return subj.entityType ?? null;
  });
  // A plain (global) conversation has NO active subject to anchor a workspace ,
  // there is nothing to show on the right. Rather than drop into the split
  // "chat panel + empty focus" editor layout, render it centered full-width
  // (ChatGPT-style): the conversation fills the main column, composer at the
  // bottom, no side panels. Scoped conversations (project / entity / tool) keep
  // the split layout because they DO have a workspace to show.
  const activeSessionHasNoSubject = useKernelStore((s) => {
    const id = s.activeChatSessionId;
    if (!id) return false;
    const session = s.inventory.ChatSession.registry[id];
    return !!session && !session.activeSubject;
  });
  // (isMasterSession is defined earlier, alongside the Analyst view-nonce bridge.)
  //
  // ONE TOOL OWNS THE SCREEN AT A TIME.
  //
  // A standalone view (Analyst, Slides, Doctrine, the admin surfaces) is an explicit
  // "this tool now owns the screen". Source is the one tool WITHOUT such a view: its
  // surface is derived from the session's subject, and a subject deliberately survives
  // navigation (setActiveSubject refuses to clear one, useChat.ts: "a chat lives where
  // it was opened"). So every path that opens another tool while a sourcing subject is
  // still active - clicking Analyst while Source's async landing is still in flight, or
  // an agent turn calling ANALYST_* from a commander session - left the Source nav row
  // lit next to the Analyst one and hung the Source setup panel on the right of the
  // Analyst grid. Two tools selected, neither surface coherent.
  //
  // Doctrine and Slides each got their own hand-written exclusion when they hit this;
  // Analyst never did. This is that exclusion stated ONCE, for every standalone view.
  const toolViewOwnsScreen = activeStandaloneView !== null;
  const isSourcingRightPanelContext = useKernelStore((s) => {
    const subj = selectActiveSubjectFromInventoryState(s);
    if (!subj || isTableFocusSubject(subj)) return false;
    // An open new-campaign form is a sourcing context whatever the agent last focused: a focus
    // on the asset without the action type closed the form's own right bar (2026-09-28).
    if (subj.focusActionType !== 'ORCHESTRATE_SOURCING' && !s.sourceCreatingCampaign) return false;
    return subj.entityType === 'Portfolio' || subj.entityType === 'Property' || subj.entityType === 'Unit';
  }) && !toolViewOwnsScreen;

  // Portfolios list open with no active sourcing setup: give the right Action panel width and a
  // "Launch a sourcing" CTA (rendered by ProjectRightSidebarGraphics) so it is not a blank strip.
  const isSourceLauncherContext = useKernelStore(
    (s) => resolvePortalViewFromTableFocus(selectActiveSubjectFromInventoryState(s)) === 'portfolios'
  ) && !toolViewOwnsScreen;
  const isOrganisationTeamsRightPanelContext = useKernelStore((s) => {
    const subj = selectActiveSubjectFromInventoryState(s);
    if (!subj || isTableFocusSubject(subj) || subj.entityType !== 'Organisation') return false;
    const organisationId = typeof subj.entityId === 'string' ? subj.entityId.trim() : '';
    if (!organisationId) return false;
    const draft = s.entityKernelState[`Organisation:${organisationId}`]?.state as { runtime?: { activeTab?: unknown } } | undefined;
    const organisation = s.inventory.Organisation.registry[organisationId] as { activeTab?: unknown; runtime?: { activeTab?: unknown } } | undefined;
    const activeTab = draft?.runtime?.activeTab ?? organisation?.activeTab ?? organisation?.runtime?.activeTab;
    return activeTab === 'TEAMS' || (activeTab === 'TEAMS_JNN' && isLegacyTeams2DUser(userContext.dbUserId));
  });

  // The right Action panel must also auto-open whenever the user is actively in a Source
  // campaign/brand context, a campaign open, a fit focused, or rows selected, not only on the
  // initial ORCHESTRATE_SOURCING launch. Without this it stays collapsed (initialWidth=0) when you
  // re-open a campaign or expand a brand, so its actions (SourceBrandPanel / bulk panel) look lost.
  // A CAMPAIGN'S CONVERSATION, OR NONE. Conversations live in campaigns: there is no dashboard
  // conversation to show, and none of a NEIGHBOURING campaign either.
  //
  // THE CHAT ARRIVES WITH THE SHORTLIST, NOT A SECOND AND A HALF LATER.
  //
  // This waited for the ACTIVE session's subject to carry the opened campaign's id. That is a
  // piece of SERVER state: openSourceCampaign resolves the conversation (~140ms), activates it,
  // and the ChatSession row that carries the binding lands in the registry later still. Measured
  // on the preview: shortlist at 278ms, chat panel at 1846ms. The panel was being withheld on
  // data it does not need - we already know a campaign is open and which conversation is being
  // activated for it.
  //
  // The flash this replaced is real and stays fixed: showing the panel while the chat still
  // belongs to ANOTHER campaign would show that campaign's conversation for as long as the swap
  // takes. So that, and only that, is what withholds it now. Opening from the dashboard - where
  // the session is bound to no campaign at all - is not that case, and is the one the user meets.
  // Source's selection, focus and draft count only inside a Source conversation (see
  // sourceStateApplies): they survive navigation on purpose, so a deny list of other tools leaked
  // them onto every surface it forgot, the Studio dashboards included.
  const sourceStateLive = useKernelStore(sourceStateApplies);
  const hasSourceRightPanelContent = useKernelStore((s) => {
    if (s.sourceFocusedFitId || (s.sourceSelectedFitIds?.length ?? 0) > 0) return true;
    const campaignId = s.sourceSelectedCampaignId;
    if (!campaignId) return false;
    // Only ASSET sourcing campaigns own a conversation. Portfolio reference shortlists and
    // unit-fit runs are different families with no such binding, so holding them to it would keep
    // their panel shut forever. Read off the OPEN campaign's own row, never a UI mode.
    if (resolveOpenCampaignScope(s) !== 'asset') return true;
    const activeId = s.activeChatSessionId;
    const subject = activeId ? s.inventory.ChatSession.registry[activeId]?.activeSubject : null;
    const boundCampaignId = (subject as { campaignId?: unknown } | null | undefined)?.campaignId;
    return typeof boundCampaignId !== 'string' || boundCampaignId === campaignId;
  }) && sourceStateLive && !toolViewOwnsScreen;
  // A DRAFT campaign is none of the three states above, there is no campaign row yet, but the
  // agent opens the campaign in the left chat before any parameter is filled in. Without this the
  // panel stays shut and the whole opening conversation happens off-screen. Lost once already, in
  // the preprod merge, because it lives in a file preprod rewrote.
  // THE CHAT COLUMN OPENS ON THE CLICK, NOT AFTER THE ROUND TRIP. Making a draft has to create a
  // session first, and the draft flag is deliberately raised only once that returns (see
  // beginSourcingCampaignDraft: raising it earlier painted the PREVIOUS conversation for a beat).
  // But gating the column on the late flag is why the screen arrived in two pieces - right bar and
  // header at 250ms, chat and canvas at 800ms. So the column opens on the preparing flag and shows
  // a placeholder until the session exists; nothing stale is ever painted.
  const sourceCampaignPreparing = useKernelStore((s) => s.sourcePreparingCampaign);
  const sourceRentRollOpen = useKernelStore(sourceRentRollOwnsCanvas) && !toolViewOwnsScreen;
  const sourceRentRollOpening = Boolean(useKernelStore(s => s.sourceOpeningRentRollId)) && !toolViewOwnsScreen;
  const sourceCampaignOpening = Boolean(useKernelStore((s) => s.sourceOpeningCampaignId)) && !toolViewOwnsScreen;
  const sourceDraftCampaignOpen = (useKernelStore((s) => s.sourceCreatingCampaign) && sourceStateLive) || sourceCampaignPreparing;

  // The left chat panel is a WORKING surface: shown ONLY when you're actively
  // working with an agent, a Studio project open, an Analyst doc open, or a
  // Source campaign/fit in focus. On plain tool dashboards (Source campaigns
  // list, Analyst with no doc, inventory, entity dashboards) it stays closed and
  // reopens when you enter a project/doc/campaign. Browsing conversations as a
  // navigation surface is a master-agent concept with its own UX, not per-tool
  // chrome. The RIGHT action panel is INDEPENDENT of this (a Source dashboard
  // keeps its setup panel while the chat is closed).
  //
  // Each clause is a SURFACE claiming the chat. A leftover subject is not a surface: a
  // Studio project or a Source campaign still anchored on the session must not open its
  // conversation beside the tool that now owns the screen (toolViewOwnsScreen). The
  // doctrine and slides clauses are exempt because they ARE the owning surface.
  const showEditorChatPanel =
    isEditorLayout &&
    !isMasterSession &&
    // The asset rent roll is a data table, not a conversation: the Source chat edits shortlists,
    // so it stays off this surface even though the asset is the session's subject.
    !sourceRentRollOpen &&
    !sourceRentRollOpening &&
    // A Project subject opens the Studio chat, but a Slides DECK IS a Project row
    // too (creationEntityType 'Project'), so this clause matched on the Slides
    // surface and outranked the deck gate below it, which is an OR alternative.
    // That is how the deck browser ended up with a chat panel beside it. The
    // surface gate has to be the authority on its own surface.
    ((activeSubjectEntityType === 'Project' && !isSlidesSurface && !toolViewOwnsScreen) ||
      analystDocOpen ||
      hasSourceRightPanelContent ||
      sourceCampaignOpening ||
      (sourceDraftCampaignOpen && !toolViewOwnsScreen) ||
      // Doctrine keeps its Master side chat: the user prompts the agent to
      // fill in their doctrine files while watching them update.
      activeStandaloneView === 'doctrine' ||
      // Slides keeps its Reviewer side chat, but only once a deck is OPEN
      // (the file browser is a full-width surface, like Figma's). Pulse likewise, once a plan is open.
      (isSlidesSurface && slidesDeckIsOpen) ||
      pulsePlanOpen);

  // DOES THE RIGHT PANEL HAVE ANYTHING TO SHOW? One predicate, used BOTH for the width it mounts
  // at and for the post-mount auto-open below, because those two were different sets and that is
  // how the panel disappeared.
  //
  // The mount width knew only about the sourcing subject and the launcher; the auto-open also knew
  // about an open campaign and the org teams tab. So for a campaign the panel mounted at width 0
  // and depended on one of those flags flipping AFTER it had mounted - the effect bails while
  // `rightActionPanelRef` is still null, and never runs again because its deps do not change. That
  // held together only while the campaign was selected LATE. Opening a campaign in one transition
  // (see openSourceCampaign) makes the flags true BEFORE the panel mounts, and the shortlist lost
  // its whole right bar.
  //
  // Mounting at the right width means the panel is correct without an effect having to fire in the
  // right order. The effect stays for the case it was written for: a flag turning true later, on a
  // panel the user has since collapsed by hand.
  // On the campaign dashboard the right bar has nothing to say: its former one control, "Create a
  // new campaign", is the glowing button at the bottom of the canvas (2026-08-23). One shared
  // predicate, so this cannot drift from the surface that renders (ProjectRightSidebarGraphics).
  const dashboardOwnsCanvas = useKernelStore((s) => sourceDashboardOwnsCanvas(s));
  // The run is launched from the bar and the chat (owner, 2026-10-05: "no purpose in having the params
  // here"): the right column opens on a run, never on the setup form, which stays mounted, collapsed,
  // for the agent's parameter commands.
  const sourceRunOpen = useKernelStore((s) => Boolean(s.sourceSelectedCampaignId) && !s.sourceCreatingCampaign);
  const rightActionPanelHasContent = !sourceRentRollOpen && (
    sourceCampaignOpening ||
    (isSourcingRightPanelContext && !dashboardOwnsCanvas && sourceRunOpen) ||
    isSourceLauncherContext ||
    hasSourceRightPanelContent ||
    isOrganisationTeamsRightPanelContext ||
    // A project's scene always has its 3D panel (layers, views, properties) to show.
    sceneChatProjectId !== null);

  useEffect(() => {
    if (!isEditorLayout) return;
    if (!rightActionPanelRef.current) return;
    // Symmetric: open when there is something to show, close when there no longer is (walking a
    // campaign back to the dashboard used to leave an empty open strip). A Studio project the user
    // already opened or closed by hand on this device keeps that state instead.
    // An open plan's panel shares this slot and always has content.
    const hasContent = rightActionPanelHasContent || pulsePlanOpen;
    if (rightActionPanelRef.current.isCurrentlyCollapsed() !== resolveBarCollapse(studioRightBarMemory, !hasContent)) {
      rightActionPanelRef.current.toggleCollapse();
    }
  }, [rightActionPanelHasContent, pulsePlanOpen, isEditorLayout, openStudioProjectId, studioRightBarMemory]);
  // Every entity detail view renders flush on a dark page (no curved canvas);
  // cards inside read as bubbles, with a stroke separating chat from content.
  const isEntityDetailView = activeSubjectEntityType !== null;
  // The Analyst workspace takes over the main content area whenever a table is
  // open or explicitly requested, EXCEPT inside a Master session, which stays on
  // the MasterConsole and surfaces model slices inline in the chat. The Master
  // builds tables in the shared store (which sets activeDocId); without this guard
  // that would immediately yank it out of its chat into the Analyst grid.
  const showAnalystWorkspace =
    canAccessAssetManagement &&
    !isMasterSession &&
    (activeStandaloneView === 'tables' ||
      (analystDocOpen && !isEntityDetailView && !toolViewOwnsScreen));
  const projectViewMode = useKernelStore((s) => {
    const subj = selectActiveSubjectFromInventoryState(s);
    if (!subj || isTableFocusSubject(subj) || subj.entityType !== 'Project' || !subj.entityId) return null;
    const project = s.inventory.Project.registry[subj.entityId];
    return project?.projectType ? resolveProjectGenerationViewMode(project.projectType) : null;
  });
  const studioProjectType = useKernelStore((s) => (studioProjectId ? s.inventory.Project.registry[studioProjectId]?.projectType : undefined));
  const accessVerdictLoaded = useKernelStore((s) => Boolean(s.userContext.accessSuspension));
  const studioProjectSection = useKernelStore((s) => resolveProjectStudioSection(studioProjectType, undefined, s.userContext.permissions));
  // Keep the existing surface predicate name; its value is now project-scoped,
  // not the global generation semantic.
  const marketingViewMode = projectViewMode;
  const studioSection = useKernelStore((s) => s.studioSection);
  const isListBatchSurface = marketingViewMode === 'marketing' && activeSubjectEntityType === 'Project';
  // Centered full-width conversation: an active session with no subject and no
  // standalone tool open. Gates OFF the left chat panel + right action panel and
  // renders the conversation centered in the main area.
  const isPlainConversation =
    isEditorLayout &&
    activeSessionHasNoSubject &&
    activeStandaloneView !== 'footfall' &&
    activeStandaloneView !== 'tables' &&
    activeStandaloneView !== 'globalActivity' &&
    activeStandaloneView !== 'finance' &&
    activeStandaloneView !== 'brandBase' &&
    activeStandaloneView !== 'organisations' &&
    // A doctrine session has no subject but is NOT a plain conversation, it
    // renders the Doctrine view with the chat in the left panel. Same for a
    // slides session (Slides view + left chat).
    activeStandaloneView !== 'doctrine' &&
    !isSlidesSurface &&
    !analystDocOpen;

  // The curved box frames only the open Studio canvas editor, whose artboard needs an edge.
  // Dashboards, grids, tables and entity pages sit straight on the black, as ChatGPT's pages do
  // (owner, 2026-09-28: 'this curved square ... for now it feels bad').
  const showCanvasBox = !isPlainConversation && !isMasterSession && activeSubjectEntityType === 'Project';

  // The shared right panel, in its two mutually exclusive forms: the action panel
  // (Studio layers / Source setup / Organisation teams) and the Slides technical bar,
  // which mounts on its own once a deck is open. Written once because the same
  // predicate is read in three places (ref cleanup, the main column's border, the
  // mount itself) and drifted between them: only the mount had been taught about
  // Doctrine. Neither form belongs to a standalone tool view except Slides' own.
  // Not on the asset rent roll either: nothing belongs beside it, and a panel mounted there kept its
  // last width as an empty strip (owner, 2026-09-26).
  // Nor on the Source dashboard with nothing to show: mounted collapsed "in case", it still drew an
  // empty outlined strip down the right of the campaign cards (owner, 2026-09-27). Opening a
  // campaign leaves the dashboard, so the panel mounts fresh, open, when it has content.
  const showRightActionPanel =
    isEditorLayout && !isListBatchSurface && !isPlainConversation && !toolViewOwnsScreen && !isSlidesSurface &&
    !sourceRentRollOpen && !sourceRentRollOpening && !(dashboardOwnsCanvas && !rightActionPanelHasContent);
  const showSlidesRightBar = isEditorLayout && isSlidesSurface && slidesDeckIsOpen;
  const showPulseRightBar = isEditorLayout && pulsePlanOpen;
  const hasRightPanel = showRightActionPanel || showSlidesRightBar || showPulseRightBar;

  // History and cold URL hydration can select a project without an explicit
  // open action. Retire its old cross-tool UI override once the type is known.
  useEffect(() => {
    if (!projectViewMode) return;
    if (studioProjectId && kernel.current) restoreProjectToolView(kernel.current, studioProjectId);
    kernel.current?.setStudioViewMode(projectViewMode);
    kernel.current?.setStudioSection(current => current === 'factory' && projectViewMode === 'marketing' && canAccessFactory ? current : studioProjectSection);
    // The kind can land after the id on a deep link; the model follows it, not only the section.
    if (studioProjectId && studioProjectType) kernel.current?.ensureDiffusionModelForProject(studioProjectId);
  }, [kernel, projectViewMode, studioProjectId, studioProjectType, studioProjectSection, canAccessFactory, accessVerdictLoaded]);
  const isStudioSurface = canAccessDesign && !toolViewOwnsScreen && !isMasterSession && !isSlidesSurface &&
    (activeTableView === 'projects' || activeSubjectEntityType === 'Project');
  const showHomeMenuDivider = !(isEditorLayout && showCanvasBox) || showEditorChatPanel;
  const hasLeftChatPanel = showEditorChatPanel && !isPlainConversation && (!isListBatchSurface || (canAccessDesign && canAccessFactory));
  const isFactorySurface = isStudioSurface && canAccessFactory && studioSection === 'factory' && (!projectViewMode || isListBatchSurface);
  const isFactoryDashboard = isFactorySurface && activeTableView === 'projects';
  // A module's home page (Studio's projects, Analyst's tables, Source's campaigns) names itself at
  // the top-left like Slides does, so it gets no navigation band above it: nothing to go back to
  // from a module's home, and the band pushed every title 40px lower than Slides' (owner, 2026-09-29).
  const isModuleDashboard =
    (activeTableView === 'projects' && !activeSubjectEntityType) ||
    activeStandaloneView === 'footfall' ||
    (activeStandaloneView === 'tables' && !analystDocOpen) ||
    dashboardOwnsCanvas;
  // An open Analyst table titles itself (back arrow, name, actions): the navigation bar above it
  // only reserved an empty band (owner, 2026-10-14).
  const analystTableOwnsTop = showAnalystWorkspace && !isModuleDashboard;
  const canvasRunsToTop = isMasterSession || isModuleDashboard || isSourcingRightPanelContext || sourceRentRollOpen
    || (isFactorySurface && !isFactoryDashboard) || analystTableOwnsTop;
  const [hasVisitedFactory, setHasVisitedFactory] = useState(false);
  if (isFactorySurface && !hasVisitedFactory) setHasVisitedFactory(true);
  // Which page the canvas shows, for its entrance animation (entity pages animate inside
  // ChatContextDashboard, keyed on their subject).
  const canvasPageKey = sourceCampaignOpening || sourceRentRollOpening ? 'source-opening'
    : showAnalystWorkspace ? 'analyst'
    : activeStandaloneView ?? (isSlidesSurface ? 'slides'
      : !activeChatSessionId ? 'chats'
      : activeSubjectEntityType === 'Project' && marketingViewMode ? 'project'
      : isMasterSession ? `master:${activeChatSessionId}` : 'dashboard');

  useEffect(() => {
    // Clear each panel's ref when that panel is not shown. They are now
    // independent: the left chat can be hidden (tool dashboard) while the right
    // action panel is still mounted (e.g. Source setup).
    if (!showEditorChatPanel) {
      kernel.current?.setLeftPanelRef(null);
    }
    if (!hasRightPanel) {
      kernel.current?.setRightPanelRef(null);
    }
  }, [kernel, showEditorChatPanel, hasRightPanel]);

  useEffect(() => {
    const kernelHandle = kernel.current;
    return () => {
      kernelHandle?.setLeftPanelRef(null);
      kernelHandle?.setRightPanelRef(null);
    };
  }, [kernel]);

  return (
    <>
      <UrlSynchronizer onSessionRestoringChange={setIsRestoringUrlSession} />
      <div data-factory-tour-root="" className="flex h-full w-full">
        <PortalHomeMenu
          onNewChat={handleHomeSidebarNewChat}
          onSearchChats={handleHomeSearchChats}
          onNavigateToView={handlePortalNavClick}
          onSource={handleHomeSource}
          onOpenOrganisationPage={handleOpenOrganisationPage}
          organisations={accessibleOrganisations}
          selectedOrganisationId={homeSelectedOrganisationId}
          onSelectOrganisation={handleSelectHomeOrganisation}
          organisationName={homeOrganisationLabel}
          activeChatSessionId={activeChatSessionId}
          isFaterAdmin={hasAdminAccess}
          masterAgentUnlocked={canAccessMasterAgent(userContext)}
          canAccessAssetManagement={canAccessAssetManagement}
          canAccessSource={canAccessSource}
          canAccessFootfall={canAccessFootfall}
          canAccessDesign={canAccessDesign}
          canAccessSlidesTool={canAccessSlidesTool}
          canAccessFater3D={canAccessFater3DModule}
          canAccessVideo={canAccessVideoModule}
          isSlidesTrial={isSlidesTrial}
          isSourceTrial={isSourceTrial}
          isCollapsed={isHomeMenuCollapsed}
          onToggleCollapse={handleToggleHomeMenuCollapse}
          activeStandaloneView={activeStandaloneView}
          onOpenSession={handleOpenSearchedSession}
          onGoHome={handleGoHome}
          // Box XOR left line: the curved box draws the content's left edge, so the
          // home-menu divider only shows on boxless surfaces (hero, plain/master chat).
          // EXCEPTION: when the left chat panel is open, the box borders the chat↔content
          // seam (far right), not the rail↔chat seam, so the rail still needs its divider.
          showDivider={showHomeMenuDivider}
        />

        {/* Home screen, full-width landing when no session and on dashboard */}
        {!isEditorLayout && (
          <div className="relative min-w-0 flex-1 bg-black">
            <PortalCanvasTopBar
              onGoHomeSource={handleHomeSource}
              showLeftControls={false}
            />
            <PageEnter pageKey={canvasPageKey} className="h-full w-full">
            {showAnalystWorkspace ? (
              <TablesWorkspace />
            ) : activeStandaloneView === 'footfall' && canAccessFootfall ? (
              <PortalInventoryView view="footfall" />
            ) : activeStandaloneView === 'globalActivity' ? (
              <PortalInventoryView view="globalActivity" onExit={handleCloseStandaloneView} />
            ) : activeStandaloneView === 'doctrine' ? (
              <PortalInventoryView view="doctrine" />
            ) : activeStandaloneView === 'fater3d' && canAccessFater3DModule ? (
              <Fater3DView />
            ) : activeStandaloneView === 'video' && canAccessVideoModule ? (
              <VideoView />
            ) : activeStandaloneView === 'finance' ? (
              <FinanceView />
            ) : activeStandaloneView === 'brandBase' ? (
              <BrandBaseView />
            ) : activeStandaloneView === 'organisations' ? (
              <OrganisationsTable />
            ) : isSlidesSurface ? (
              <PortalInventoryView view="slides" />
            ) : (
              <GlobalChatList />
            )}
            </PageEnter>
          </div>
        )}
        {/* Editor layout, panel + main content (Studio-style) */}
        {isEditorLayout && (
          <div className={clsx('relative flex h-full w-full min-w-0 overflow-hidden', isStudioSurface && (!isFactorySurface || isFactoryDashboard) && 'max-sm:pt-[44px]')}>
            {/* Left Chat Panel, hidden for a plain conversation, which renders
                the chat centered in the main area instead. */}
            {showEditorChatPanel && !isPlainConversation && (!isListBatchSurface || (canAccessDesign && canAccessFactory)) && (
              <ResizablePanel
                ref={bindLeftPanelRef}
                isVisible={!isFactorySurface}
                side="left"
                initialWidth="30%"
                startCollapsed={isPulseSession ? !pulseChatOpen : studioLeftBarMemory === true}
                onUserCollapseChange={handleStudioLeftBarGesture}
                minWidthPx={300}
                maxWidthPx={800}
                collapsedWidthPx={8}
                collapseThresholdFactor={0.6}
                className={clsx('left-sidebar-resizable-panel bg-black', isStudioSurface && 'max-w-full !min-w-0 [&>.resizable-panel-content-host]:!min-w-0 [&>.resizable-panel-content-host]:max-w-[calc(100%_-_12px)]')}
                style={{ marginRight: '-4px'}}
              >
                <div
                  style={{ maxHeight: '100%', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                  className="py-2 pl-2"
                >
                  <div className="flex flex-col flex-1 min-h-0 w-full max-w-[760px] overflow-hidden">
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', color: 'white' }}>
                      {sourceCampaignPreparing || sourceCampaignOpening || sourceRentRollOpening ? (
                        // THE SKELETON IS THE GREETING, MINUS THE WORDS.
                        //
                        // The session for this draft does not exist yet. Rendering ChatTab here
                        // would show the conversation being left behind, which is the flash the
                        // draft ordering exists to prevent - so the column holds its shape and
                        // waits. What it waits with is what is about to arrive: the agent opens a
                        // draft with a two-paragraph greeting, above the composer.
                        //
                        // Same doctrine as the campaign card skeleton, and the same reason: mirror
                        // the RENDERED boxes, not the font sizes, because globals.css clamps type
                        // app-wide so a 13px font is not a 13px line. Measured in the browser on a
                        // real draft: 13px type in an 18.85px line box, paragraphs 377px wide in a
                        // 398px column, 6.5px between them, composer 103px tall.
                        <div
                          className="flex h-full w-full flex-col justify-end px-2 pb-2"
                          aria-hidden="true"
                        >
                          {[
                            [94, 88, 61],
                            [92, 97, 85, 47],
                          ].map((lineWidths, paragraph) => (
                            <div
                              key={`greeting-paragraph-${paragraph}`}
                              className="flex w-[94%] flex-col"
                              style={{ marginBottom: paragraph === 0 ? 6.5 : 0 }}
                            >
                              {lineWidths.map((width, line) => (
                                // The bar sits INSIDE the line box rather than filling it, so the
                                // block reads as text rather than as a filled rectangle, while
                                // still occupying exactly the height the paragraph will.
                                <div key={line} className="flex h-[18.85px] items-center">
                                  <div
                                    className="h-[13px] animate-pulse rounded-[3px] bg-white/[0.055]"
                                    style={{
                                      width: `${width}%`,
                                      // Staggered, so it reads as a sentence arriving rather than
                                      // one slab breathing.
                                      animationDelay: `${(paragraph * 4 + line) * 90}ms`,
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          ))}
                          {/* The composer is chrome: it arrives identical, so its shell is drawn
                              for real and does NOT pulse. Only what it will be filled with does. */}
                          <div className="mt-3 h-[103px] w-full rounded-[28px] border border-white/10 bg-white/[0.04] px-4 py-4">
                            <div className="h-[13px] w-[86px] animate-pulse rounded-[3px] bg-white/[0.055]" />
                            <div className="mt-[33px] flex items-center gap-2">
                              <div className="h-[18px] w-[18px] animate-pulse rounded-full bg-white/[0.05]" />
                              <div className="h-[18px] w-[54px] animate-pulse rounded-[5px] bg-white/[0.05]" />
                              <div className="ml-auto h-[18px] w-[18px] animate-pulse rounded-full bg-white/[0.05]" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Propless, from preprod: the 28 commits merged here moved these panels onto
                        // their own selectors, so ChatTab reads the assistant model and the
                        // organisation from the store itself. Passing them again would re-introduce
                        // exactly the prop drilling that refactor removed.
                        <>
                          {sceneMountedProjectId ? (
                            <div inert={!sceneChatProjectId} style={{ display: sceneChatProjectId ? 'contents' : 'none' }}>
                              <Fater3DSceneChat projectId={sceneMountedProjectId} />
                            </div>
                          ) : null}
                          <div inert={!!sceneChatProjectId} style={{ display: sceneChatProjectId ? 'none' : 'contents' }}>
                            <ChatTab />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </ResizablePanel>
            )}

            {/* Main content area, canvas-style like Studio */}
            <div
              className={clsx(
                '@container/studio flex-1 min-w-0 h-full relative bg-black',
                // An open Source campaign runs edge to edge, so its working glow lights the whole
                // column; the 8px frame left a dark band inside it (owner, 2026-09-29).
                isModuleDashboard || isSourcingRightPanelContext ? 'py-0' : 'py-2',
                // Module homes carry their own 24px margin (InventoryListToolbar); the 8px frame is
                // for the boxed canvases only, and put Studio and Analyst 8px off Slides and Source.
                !isSourcingRightPanelContext && !isModuleDashboard && 'pr-2',
                isStudioSurface && 'max-sm:static',
                // The curved box already draws the content's right edge, so the main/right-panel
                // divider is only needed on headerless (boxless) surfaces - otherwise it doubles
                // up 9px outside the box border. Doctrine keeps its own layout (no divider).
                hasLeftChatPanel && !isFactorySurface && !showCanvasBox && 'border-l border-white/[0.08]'
              )}
            >
              {/* Master chats render flush like ChatGPT/Claude: no back/forward
                  arrows, no "Sans sujets" title bar. */}
              {analystTableOwnsTop ? null : !isMasterSession && !isModuleDashboard && (!isFactorySurface || isFactoryDashboard) ? (
                <PortalCanvasTopBar onGoHomeSource={handleHomeSource} isStudioLayout={isStudioSurface} floating={isSourcingRightPanelContext || sourceRentRollOpen} />
              ) : (
                <>
                  <PortalCanvasTopBar onGoHomeSource={handleHomeSource} accountOnly />
                  {pulsePlanOpen && <PlanTopBar />}
                </>
              )}
              <div
                // A stable handle on "the main content area", for a surface that needs to take it
                // over from deep inside the tree without being re-parented through every component
                // in between. Currently the Source store map, whose Full view promotes it here and
                // leaves the left chat and the right panel exactly where they are.
                id="portal-main-canvas"
                className={clsx('relative w-full overflow-hidden', isStudioSurface && 'max-sm:!mt-0 max-sm:!h-full')}
                style={{
                  // Source runs to the top: its navigation floats over the page (see the top bar).
                  marginTop: canvasRunsToTop ? '0px' : '33px',
                  height: canvasRunsToTop ? '100%' : 'calc(100% - 33px)',
                  borderRadius: showCanvasBox ? '25px' : '0px',
                  border: showCanvasBox ? '1px solid rgba(51, 51, 51, 1)' : 'none',
                  background: '#000000',
                }}
              >
                {hasVisitedFactory && canAccessFactory && <div data-studio-surface={isFactorySurface ? 'active' : undefined} inert={!isFactorySurface} style={{ display: isFactorySurface ? 'block' : 'none' }} className="h-full min-h-0"><PageEnter pageKey={isFactorySurface ? 'factory' : 'hidden'} className="h-full min-h-0"><FactoryScreen active={isFactorySurface} /></PageEnter></div>}
                <div data-studio-surface={!isFactorySurface ? 'active' : undefined} inert={isFactorySurface} style={{ display: isFactorySurface ? 'none' : 'contents' }}>
                <PageEnter pageKey={canvasPageKey} className="relative h-full w-full">
                {sourceCampaignOpening || sourceRentRollOpening ? <SourceCampaignLoading /> : showAnalystWorkspace ? (
                  <TablesWorkspace embedded />
                ) : activeStandaloneView === 'footfall' && canAccessFootfall ? (
                  <PortalInventoryView view="footfall" />
                ) : activeStandaloneView === 'globalActivity' ? (
                  <PortalInventoryView view="globalActivity" onExit={handleCloseStandaloneView} />
                ) : activeStandaloneView === 'doctrine' ? (
                  <PortalInventoryView view="doctrine" />
                ) : activeStandaloneView === 'finance' ? (
                  <FinanceView />
                ) : activeStandaloneView === 'brandBase' ? (
                  <BrandBaseView />
                ) : activeStandaloneView === 'organisations' ? (
                  <OrganisationsTable />
                ) : isSlidesSurface ? (
                  <PortalInventoryView view="slides" />
                ) : !activeChatSessionId ? (
                  <GlobalChatList />
                ) : activeSubjectEntityType === 'Project' && marketingViewMode ? (
                  <>
                    <div
                      className="absolute inset-0 h-full w-full"
                      style={{
                        opacity: marketingViewMode === 'marketing' ? 1 : 0,
                        transform: marketingViewMode === 'marketing' ? 'translateY(0)' : 'translateY(4px)',
                        transition: 'opacity 100ms ease-out, transform 100ms ease-out',
                        visibility: marketingViewMode === 'marketing' ? 'visible' : 'hidden',
                        pointerEvents: marketingViewMode === 'marketing' ? 'auto' : 'none',
                        willChange: 'opacity, transform',
                      }}
                    >
                      {marketingViewMode === 'marketing' && canAccessDesign && canAccessFactory && <MarketingBatchCanvas />}
                    </div>
                    <div
                      className="absolute inset-0 h-full w-full"
                      style={{
                        opacity: marketingViewMode === 'studio' ? 1 : 0,
                        transform: marketingViewMode === 'studio' ? 'translateY(0)' : 'translateY(4px)',
                        transition: 'opacity 100ms ease-out, transform 100ms ease-out',
                        visibility: marketingViewMode === 'studio' ? 'visible' : 'hidden',
                        pointerEvents: marketingViewMode === 'studio' ? 'auto' : 'none',
                        willChange: 'opacity, transform',
                      }}
                    >
                      {marketingViewMode === 'studio' && <ChatContextDashboard />}
                    </div>
                  </>
                ) : isMasterSession ? (
                  // MASTER'S CONSOLE RENDERS FOR MASTER SESSIONS. NOTHING ELSE.
                  //
                  // This used to catch `isPlainConversation` too - any subjectless chat - to spare
                  // legacy 'global' sessions a blank page. But "subjectless" is also the state a
                  // TOOL session passes through: clicking a campaign in Source lands on a session
                  // before its subject is attached, so the user got Master's empty "Ask anything"
                  // screen where their shortlist should be, with no way to tell what had happened.
                  // Every other tool surface was already excluded from that predicate one at a
                  // time (tables, activity, doctrine, slides); Source could not be,
                  // because it is not a standalone view. Owning the rule here fixes it for every
                  // surface at once, including the next one nobody remembers to add.
                  <MasterConsole />
                ) : (
                  <div className="relative flex h-full w-full flex-col bg-black">
                    <div className="relative z-10 flex h-full w-full flex-col">
                      <ChatContextDashboard />
                    </div>
                  </div>
                )}
                </PageEnter>
                </div>
              </div>
            </div>

            {/* Right Action Panel, never for List projects: the batch surface
                has no canvas concepts (layers/graphics), so the whole panel and
                its expand affordances stay out of the tree. Never for a standalone
                tool view either (Doctrine, Analyst, the admin surfaces): those own
                the screen, and the Source targeting panel must never hang off the
                side of one. They slip past !isPlainConversation on purpose (they
                keep their left chat), so the exclusion has to be explicit. */}
            {showRightActionPanel && !isFactorySurface && (
              <ResizablePanel
                ref={bindRightPanelRef}
                side="right"
                // Initialize EXPANDED whenever the panel has something to show, so its controls are
                // visible by default. Starting at 0 made it init collapsed and the post-mount
                // toggle didn't reliably re-open it - which is exactly what happened to the
                // shortlist once the campaign was selected before this mounted.
                initialWidth={studioRightBarMemory === false ? RIGHT_ACTION_PANEL_MIN_WIDTH_PX : studioRightBarMemory === null && rightActionPanelHasContent ? 320 : 0}
                minWidthPx={RIGHT_ACTION_PANEL_MIN_WIDTH_PX}
                maxWidthPx={1280}
                collapsedWidthPx={0}
                collapseThresholdFactor={0.6}
                // A collapsed panel with nothing to show refuses the expand gestures: on the
                // dashboard a double-click on the handle opened an empty strip (2026-08-23). A Studio
                // project always has its own panel to show.
                disableExpandGesture={!rightActionPanelHasContent && !openStudioProjectId}
                onUserCollapseChange={handleStudioRightBarGesture}
                onWidthChange={(_, collapsed) => setIsRightPanelCollapsed(collapsed)}
                // An open Source campaign: the drag handle lies over the column instead of beside it,
                // on a clear background, so the working glow reaches the panel's edge line (owner,
                // 2026-09-29). Elsewhere the handle keeps its black strip.
                className={clsx('right-sidebar-resizable-panel', isSourcingRightPanelContext ? '[&>.resizable-panel-content-host]:bg-black' : 'bg-black')}
                // The overlap tucks an OPEN panel's handle over the column; a collapsed one kept it and
                // pushed every Studio home 4px past the screen's edge.
                style={{ marginLeft: isRightPanelCollapsed ? 0 : isSourcingRightPanelContext ? '-12px' : '-4px' }}
              >
                <div
                  style={{ maxHeight: '100%', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                  className={clsx('py-2 pr-2', !showCanvasBox && 'border-l border-white/[0.08]')}
                >
                  {sourceCampaignOpening ? <SourceCampaignLoading sidebar /> : (
                    <>
                      {sceneMountedProjectId ? (
                        // Studio's drop handler leaves this column to the scene, whose own drop handler its React tree reaches.
                        <div ref={bindScenePanelHost} inert={!sceneChatProjectId} style={{ display: sceneChatProjectId ? 'flex' : 'none' }} className="min-h-0 w-full flex-1 flex-col" data-generic-image-input-drop-zone="true" />
                      ) : null}
                      <div inert={!!sceneChatProjectId} style={{ display: sceneChatProjectId ? 'none' : 'contents' }}>
                        <ProjectRightSidebarGraphics />
                      </div>
                    </>
                  )}
                </div>
              </ResizablePanel>
            )}

            {showPulseRightBar && (
              <ResizablePanel
                ref={bindRightPanelRef}
                side="right"
                initialWidth={400}
                minWidthPx={320}
                maxWidthPx={760}
                collapsedWidthPx={0}
                collapseThresholdFactor={0.6}
                onWidthChange={(_, collapsed) => setIsRightPanelCollapsed(collapsed)}
                // As an open Source campaign: the handle lies over the map, so no black strip separates them.
                className="right-sidebar-resizable-panel [&>.resizable-panel-content-host]:bg-black"
                style={{ marginLeft: '-12px' }}
              >
                <div className="flex h-full w-full flex-col items-center border-l border-white/[0.08] py-2 pr-2">
                  <PulseRightSidebar onSource={handleFootfallSource} />
                </div>
              </ResizablePanel>
            )}

            {/* Slides right technical bar, the same shared right panel slot Studio
                uses, mounted only when a deck is open (the file browser is full-width). */}
            {showSlidesRightBar && (
              <ResizablePanel
                ref={bindRightPanelRef}
                side="right"
                initialWidth={320}
                minWidthPx={240}
                maxWidthPx={1280}
                collapsedWidthPx={0}
                collapseThresholdFactor={0.6}
                onWidthChange={(_, collapsed) => setIsRightPanelCollapsed(collapsed)}
                className="right-sidebar-resizable-panel bg-black"
                style={{ marginLeft: '-4px' }}
              >
                <div
                  className={clsx('py-2 pr-2', !showCanvasBox && 'border-l border-white/[0.08]')}
                  style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                  <SlidesRightSidebar />
                </div>
              </ResizablePanel>
            )}
          </div>
        )}
      </div>
      {isChatSearchOpen && (
        <ChatSearchModal
          isOpen={isChatSearchOpen}
          onClose={handleCloseChatSearchModal}
          onOpenSession={handleOpenSearchedSession}
          onNewChat={handleHomeNewChat}
        />
      )}
      {isSourcingRightPanelContext && isSourceTrial && <SourceTrialWelcome />}
</>
  );
};

export const Portal = memo(PortalComponent);
